import React from 'react'
// c:\xampp\htdocs\miners-market-main\components\KgAnalytics.js

function KgAnalytics() {
    const [analytics, setAnalytics] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [activeTab, setActiveTab] = React.useState('overview');
    const [isDownloading, setIsDownloading] = React.useState(false);

    // Date range state
    const today = new Date();
    const thirtyDaysAgo = new Date(new Date().setDate(today.getDate() - 30));
    const [startDate, setStartDate] = React.useState(thirtyDaysAgo.toISOString().split('T')[0]);
    const [endDate, setEndDate] = React.useState(today.toISOString().split('T')[0]);

    const fetchAnalytics = async (metric = 'overview') => {
        try {
            setLoading(true);
            setError(null);
            const url = `/api/admin_get_kg_analytics.php?metric=${metric}&start_date=${startDate}&end_date=${endDate}`;
            const response = await fetch(url, { credentials: 'include' });
            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || errData.message || 'Failed to fetch analytics data');
            }
            const data = await response.json();
            if (data.setup_required) {
                throw new Error('KG Analytics tables not found. Please run the migration script: api/migrate_kg_analytics_tables.php');
            }
            setAnalytics(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        fetchAnalytics(activeTab);
    }, [activeTab, startDate, endDate]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    const handleDownloadPdf = async () => {
        setIsDownloading(true);
        setError(null);
        try {
            // Fetch all data for the report
            const [overviewRes, categoriesRes, geographyRes, growthRes, enquiriesRes, priceRequestsRes, basketQuotesRes] = await Promise.all([
                fetch(`/api/admin_get_kg_analytics.php?metric=overview&start_date=${startDate}&end_date=${endDate}`, { credentials: 'include' }),
                fetch(`/api/admin_get_kg_analytics.php?metric=categories&start_date=${startDate}&end_date=${endDate}`, { credentials: 'include' }),
                fetch(`/api/admin_get_kg_analytics.php?metric=geography&start_date=${startDate}&end_date=${endDate}`, { credentials: 'include' }),
                fetch(`/api/admin_get_kg_analytics.php?metric=growth&start_date=${startDate}&end_date=${endDate}`, { credentials: 'include' }),
                fetch(`/api/admin_get_kg_analytics.php?metric=enquiries&start_date=${startDate}&end_date=${endDate}`, { credentials: 'include' }),
                fetch(`/api/admin_get_kg_analytics.php?metric=price_requests&start_date=${startDate}&end_date=${endDate}`, { credentials: 'include' }),
                fetch(`/api/admin_get_kg_analytics.php?metric=basket_quotes&start_date=${startDate}&end_date=${endDate}`, { credentials: 'include' })
            ]);

            const overview = await overviewRes.json();
            const categories = await categoriesRes.json();
            const geography = await geographyRes.json();
            const growth = await growthRes.json();
            const enquiries = await enquiriesRes.json();
            const priceRequests = await priceRequestsRes.json();
            const basketQuotes = await basketQuotesRes.json();

            // Initialize jsPDF
            const { jsPDF } = window.jspdf;
            const doc = new jsPDF();

            // Add logos
            const minersMarketLogo = new Image();
            minersMarketLogo.src = 'images/logo/MM Logo.jpg';
            await new Promise(resolve => { minersMarketLogo.onload = resolve; });
            doc.addImage(minersMarketLogo, 'JPEG', 15, 15, 30, 30);

            // Add KG logo if available
            const kgLogo = new Image();
            kgLogo.src = 'app/kp-logos/Rectangle.png';
            await new Promise(resolve => { kgLogo.onload = resolve; });
            doc.addImage(kgLogo, 'PNG', doc.internal.pageSize.getWidth() - 45, 15, 30, 30);

            // Title
            doc.setFontSize(22);
            doc.setFont('helvetica', 'bold');
            doc.text('KG Analytics Report', doc.internal.pageSize.getWidth() / 2, 30, { align: 'center' });
            doc.setFontSize(14);
            doc.setFont('helvetica', 'normal');
            doc.text('KG International - All Products', doc.internal.pageSize.getWidth() / 2, 38, { align: 'center' });
            doc.setFontSize(10);
            doc.setTextColor(100, 100, 100);
            doc.text(`Report Period: ${startDate} to ${endDate}`, doc.internal.pageSize.getWidth() / 2, 45, { align: 'center' });
            doc.text(`Generated: ${new Date().toLocaleString()}`, doc.internal.pageSize.getWidth() / 2, 50, { align: 'center' });

            let yPos = 60;

            // Overview Section
            doc.setFontSize(16);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(33, 33, 33);
            doc.text('Executive Summary', 15, yPos);
            yPos += 10;

            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            const overviewData = [
                ['Total Views', overview.total_views?.toString() || '0'],
                ['Total Clicks', overview.total_clicks?.toString() || '0'],
                ['Unique Clicks', overview.unique_clicks?.toString() || '0'],
                ['Total Enquiries', overview.total_enquiries?.toString() || '0'],
                ['Total Price Requests', overview.total_price_requests?.toString() || '0'],
                ['Total Basket Quotes', overview.total_basket_quotes?.toString() || '0'],
                ['Products Tracked', overview.total_products?.toString() || '0']
            ];
            doc.autoTable({
                startY: yPos,
                head: [['Metric', 'Value']],
                body: overviewData,
                theme: 'grid',
                headStyles: { fillColor: [22, 160, 133] },
                styles: { fontSize: 9 }
            });
            yPos = doc.lastAutoTable.finalY + 15;

            // Data Source Note
            doc.setFontSize(9);
            doc.setFont('helvetica', 'italic');
            doc.setTextColor(100, 100, 100);
            doc.text('Data Sources: Product-level views/clicks from KG product tracking + Supplier-level analytics from existing supplier analytics system', 15, yPos);
            yPos += 10;
            doc.setTextColor(0, 0, 0);

            // Category Performance
            if (categories.categories && categories.categories.length > 0) {
                if (yPos > 250) {
                    doc.addPage();
                    yPos = 20;
                }
                doc.setFontSize(16);
                doc.setFont('helvetica', 'bold');
                doc.text('Views by Product Category', 15, yPos);
                yPos += 10;

                const categoryData = categories.categories.map(cat => [
                    cat.category,
                    cat.total_views?.toString() || '0',
                    cat.total_clicks?.toString() || '0',
                    cat.unique_products?.toString() || '0'
                ]);
                doc.autoTable({
                    startY: yPos,
                    head: [['Category', 'Views', 'Clicks', 'Products']],
                    body: categoryData,
                    theme: 'grid',
                    headStyles: { fillColor: [22, 160, 133] },
                    styles: { fontSize: 9 }
                });
                yPos = doc.lastAutoTable.finalY + 15;
            }

            // Geography Section
            if (geography.geography && geography.geography.length > 0) {
                if (yPos > 250) {
                    doc.addPage();
                    yPos = 20;
                }
                doc.setFontSize(16);
                doc.setFont('helvetica', 'bold');
                doc.text('Buyer Geography & Industry', 15, yPos);
                yPos += 10;

                const geoData = geography.geography.map(g => [
                    g.location || 'N/A',
                    g.industry || 'N/A',
                    g.total_enquiries?.toString() || '0',
                    g.unique_buyers?.toString() || '0'
                ]);
                doc.autoTable({
                    startY: yPos,
                    head: [['Location', 'Industry', 'Enquiries', 'Buyers']],
                    body: geoData,
                    theme: 'grid',
                    headStyles: { fillColor: [22, 160, 133] },
                    styles: { fontSize: 9 }
                });
                yPos = doc.lastAutoTable.finalY + 15;
            }

            // Growth Section
            if (growth.growth && growth.growth.length > 0) {
                if (yPos > 250) {
                    doc.addPage();
                    yPos = 20;
                }
                doc.setFontSize(16);
                doc.setFont('helvetica', 'bold');
                doc.text('Month-on-Month Growth', 15, yPos);
                yPos += 10;

                const growthData = growth.growth.map(g => [
                    g.month,
                    g.views?.toString() || '0',
                    g.clicks?.toString() || '0',
                    g.enquiries?.toString() || '0',
                    (g.views_growth >= 0 ? '+' : '') + g.views_growth + '%'
                ]);
                doc.autoTable({
                    startY: yPos,
                    head: [['Month', 'Views', 'Clicks', 'Enquiries', 'Views Growth']],
                    body: growthData,
                    theme: 'grid',
                    headStyles: { fillColor: [22, 160, 133] },
                    styles: { fontSize: 9 }
                });
                yPos = doc.lastAutoTable.finalY + 15;
            }

            // Enquiries Section
            if (enquiries.enquiries && enquiries.enquiries.length > 0) {
                if (yPos > 250) {
                    doc.addPage();
                    yPos = 20;
                }
                doc.setFontSize(16);
                doc.setFont('helvetica', 'bold');
                doc.text('Recent Enquiries/RFQs', 15, yPos);
                yPos += 10;

                const enquiryData = enquiries.enquiries.slice(0, 20).map(e => [
                    e.product_name,
                    e.buyer_name,
                    e.buyer_location || 'N/A',
                    e.buyer_industry || 'N/A',
                    e.status
                ]);
                doc.autoTable({
                    startY: yPos,
                    head: [['Product', 'Buyer', 'Location', 'Industry', 'Status']],
                    body: enquiryData,
                    theme: 'grid',
                    headStyles: { fillColor: [22, 160, 133] },
                    styles: { fontSize: 8 }
                });
            }

            // Price Requests Section
            if (priceRequests.price_requests && priceRequests.price_requests.length > 0) {
                if (yPos > 250) {
                    doc.addPage();
                    yPos = 20;
                }
                doc.setFontSize(16);
                doc.setFont('helvetica', 'bold');
                doc.text('Price Requests by Category', 15, yPos);
                yPos += 10;

                const priceRequestData = priceRequests.price_requests.slice(0, 20).map(pr => [
                    pr.category || 'N/A',
                    pr.product_type || 'N/A',
                    pr.total_requests?.toString() || '0',
                    pr.unique_products?.toString() || '0',
                    pr.unique_buyers?.toString() || '0'
                ]);
                doc.autoTable({
                    startY: yPos,
                    head: [['Category', 'Product Type', 'Requests', 'Products', 'Buyers']],
                    body: priceRequestData,
                    theme: 'grid',
                    headStyles: { fillColor: [22, 160, 133] },
                    styles: { fontSize: 8 }
                });
            }

            // Basket Quotes Section
            if (basketQuotes.basket_quotes && basketQuotes.basket_quotes.length > 0) {
                if (yPos > 250) {
                    doc.addPage();
                    yPos = 20;
                }
                doc.setFontSize(16);
                doc.setFont('helvetica', 'bold');
                doc.text('Basket Quotes by Category', 15, yPos);
                yPos += 10;

                const basketQuoteData = basketQuotes.basket_quotes.slice(0, 20).map(bq => [
                    bq.category || 'N/A',
                    bq.product_type || 'N/A',
                    bq.total_quotes?.toString() || '0',
                    bq.unique_products?.toString() || '0',
                    bq.unique_buyers?.toString() || '0',
                    bq.total_quantity?.toString() || '0'
                ]);
                doc.autoTable({
                    startY: yPos,
                    head: [['Category', 'Product Type', 'Quotes', 'Products', 'Buyers', 'Quantity']],
                    body: basketQuoteData,
                    theme: 'grid',
                    headStyles: { fillColor: [22, 160, 133] },
                    styles: { fontSize: 8 }
                });
            }

            // Footer
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(100, 100, 100);
                doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.getWidth() / 2, 287, { align: 'center' });
                doc.text('Miners Market | KG Analytics | Confidential', 15, 287);
            }

            doc.save(`KG_Analytics_Report_${startDate}_to_${endDate}.pdf`);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsDownloading(false);
        }
    };

    const tabs = [
        { id: 'overview', label: 'Overview', icon: 'fa-chart-pie' },
        { id: 'categories', label: 'Product Categories', icon: 'fa-th-large' },
        { id: 'geography', label: 'Buyer Geography', icon: 'fa-map-marker-alt' },
        { id: 'growth', label: 'Monthly Growth', icon: 'fa-chart-line' },
        { id: 'enquiries', label: 'Enquiries/RFQs', icon: 'fa-envelope' },
        { id: 'price_requests', label: 'Price Requests', icon: 'fa-tag' },
        { id: 'basket_quotes', label: 'Basket Quotes', icon: 'fa-shopping-basket' }
    ];

    return (
        <>
            {/* Date Filter */}
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <h2 className="text-2xl font-bold text-gray-900">KG Analytics Dashboard</h2>
                    <div className="flex items-center gap-2 flex-wrap">
                        <div>
                            <label htmlFor="start-date" className="text-sm font-medium text-gray-700">From:</label>
                            <input
                                type="date"
                                id="start-date"
                                value={startDate}
                                onChange={e => setStartDate(e.target.value)}
                                className="ml-1 border-gray-300 rounded-md shadow-sm text-sm"
                            />
                        </div>
                        <div>
                            <label htmlFor="end-date" className="text-sm font-medium text-gray-700">To:</label>
                            <input
                                type="date"
                                id="end-date"
                                value={endDate}
                                onChange={e => setEndDate(e.target.value)}
                                className="ml-1 border-gray-300 rounded-md shadow-sm text-sm"
                            />
                        </div>
                        <button
                            onClick={() => fetchAnalytics(activeTab)}
                            className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 text-sm"
                        >
                            <i className="fas fa-filter mr-1"></i> Apply
                        </button>
                        <button
                            onClick={handleDownloadPdf}
                            disabled={isDownloading}
                            className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 text-sm disabled:opacity-50"
                        >
                            {isDownloading
                                ? <><i className="fas fa-spinner fa-spin mr-1"></i>Generating...</>
                                : <><i className="fas fa-file-pdf mr-1"></i>Download Report</>
                            }
                        </button>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-xl shadow-lg mb-6">
                <div className="border-b border-gray-200">
                    <nav className="flex -mb-px overflow-x-auto">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => handleTabChange(tab.id)}
                                className={`py-4 px-6 text-sm font-medium whitespace-nowrap border-b-2 ${
                                    activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                <i className={`fas ${tab.icon} mr-2`}></i>
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6">
                    {error}
                </div>
            )}

            {/* Loading State */}
            {loading && (
                <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                    <i className="fas fa-spinner fa-spin text-4xl text-blue-400 mb-4"></i>
                    <p className="text-gray-600">Loading analytics data...</p>
                </div>
            )}

            {/* Content */}
            {!loading && !error && analytics && (
                <>
                    {/* Overview Tab */}
                    {activeTab === 'overview' && (
                        <div className="space-y-6">
                            {/* Key Metrics Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                <div className="bg-white rounded-xl shadow-lg p-6">
                                    <div className="flex items-center">
                                        <div className="p-3 bg-blue-100 rounded-full">
                                            <i className="fas fa-eye text-blue-600 text-xl"></i>
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm text-gray-500">Total Views</p>
                                            <p className="text-2xl font-bold text-gray-900">{analytics.total_views || 0}</p>
                                            {analytics.existing_views > 0 && (
                                                <p className="text-xs text-gray-400">{analytics.existing_views} from supplier analytics</p>
                                            )}
                                            {analytics.new_views > 0 && (
                                                <p className="text-xs text-gray-400">{analytics.new_views} from product tracking</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white rounded-xl shadow-lg p-6">
                                    <div className="flex items-center">
                                        <div className="p-3 bg-green-100 rounded-full">
                                            <i className="fas fa-mouse-pointer text-green-600 text-xl"></i>
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm text-gray-500">Total Clicks</p>
                                            <p className="text-2xl font-bold text-gray-900">{analytics.total_clicks || 0}</p>
                                            {analytics.existing_clicks > 0 && (
                                                <p className="text-xs text-gray-400">{analytics.existing_clicks} from supplier analytics</p>
                                            )}
                                            {analytics.new_clicks > 0 && (
                                                <p className="text-xs text-gray-400">{analytics.new_clicks} from product tracking</p>
                                            )}
                                            {analytics.unique_clicks > 0 && (
                                                <p className="text-xs text-gray-400">{analytics.unique_clicks} unique clicks</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white rounded-xl shadow-lg p-6">
                                    <div className="flex items-center">
                                        <div className="p-3 bg-purple-100 rounded-full">
                                            <i className="fas fa-envelope text-purple-600 text-xl"></i>
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm text-gray-500">Total Enquiries</p>
                                            <p className="text-2xl font-bold text-gray-900">{analytics.total_enquiries || 0}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white rounded-xl shadow-lg p-6">
                                    <div className="flex items-center">
                                        <div className="p-3 bg-orange-100 rounded-full">
                                            <i className="fas fa-boxes text-orange-600 text-xl"></i>
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm text-gray-500">Products Tracked</p>
                                            <p className="text-2xl font-bold text-gray-900">{analytics.total_products || 0}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white rounded-xl shadow-lg p-6">
                                    <div className="flex items-center">
                                        <div className="p-3 bg-indigo-100 rounded-full">
                                            <i className="fas fa-tag text-indigo-600 text-xl"></i>
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm text-gray-500">Price Requests</p>
                                            <p className="text-2xl font-bold text-gray-900">{analytics.total_price_requests || 0}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white rounded-xl shadow-lg p-6">
                                    <div className="flex items-center">
                                        <div className="p-3 bg-teal-100 rounded-full">
                                            <i className="fas fa-shopping-basket text-teal-600 text-xl"></i>
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm text-gray-500">Basket Quotes</p>
                                            <p className="text-2xl font-bold text-gray-900">{analytics.total_basket_quotes || 0}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Data Source Note */}
                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4">
                                <p className="text-sm text-blue-800">
                                    <i className="fas fa-info-circle mr-2"></i>
                                    <strong>Data Sources:</strong> Views and clicks combine product-level tracking (new) with existing supplier-level analytics from the supplier analytics system.
                                    Supplier: <strong>KG INTERNATIONAL</strong> |
                                    Existing Views: <strong>{analytics.existing_views || 0}</strong> |
                                    Existing Clicks: <strong>{analytics.existing_clicks || 0}</strong> |
                                    Unique Clicks: <strong>{analytics.unique_clicks || 0}</strong>
                                </p>
                            </div>

                            {/* Category Breakdown */}
                            {analytics.category_breakdown && analytics.category_breakdown.length > 0 && (
                                <div className="bg-white rounded-xl shadow-lg p-6">
                                    <h3 className="text-lg font-bold text-gray-900 mb-4">Category Performance</h3>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Views</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Products</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {analytics.category_breakdown.map((cat, idx) => (
                                                    <tr key={idx}>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 capitalize">{cat.category}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{cat.views}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{cat.products}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Recent Activity Chart */}
                            {(analytics.recent_views || analytics.recent_clicks) && (
                                <div className="bg-white rounded-xl shadow-lg p-6">
                                    <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Activity (Last 30 Days)</h3>
                                    <div className="h-64">
                                        <SimpleLineChart
                                            viewsData={analytics.recent_views || []}
                                            clicksData={analytics.recent_clicks || []}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Categories Tab */}
                    {activeTab === 'categories' && analytics.categories && (
                        <div className="bg-white rounded-xl shadow-lg p-6">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Views by Product Category</h3>
                            <p className="text-sm text-gray-600 mb-4">Identify which KG products are attracting the most interest</p>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Views</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unique Products</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unique Viewers</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Clicks</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Click Rate</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {analytics.categories.map((cat, idx) => (
                                            <tr key={idx}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 capitalize">{cat.category}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{cat.total_views}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{cat.unique_products}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{cat.unique_viewers}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{cat.total_clicks || 0}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {cat.total_views > 0 ? ((cat.total_clicks || 0) / cat.total_views * 100).toFixed(1) : 0}%
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Geography Tab */}
                    {activeTab === 'geography' && (
                        <div className="space-y-6">
                            {analytics.geography && analytics.geography.length > 0 && (
                                <div className="bg-white rounded-xl shadow-lg p-6">
                                    <h3 className="text-lg font-bold text-gray-900 mb-4">Buyer Geography & Sub-Industry</h3>
                                    <p className="text-sm text-gray-600 mb-4">Understand where KG is gaining the most traction and which industries are showing the highest interest</p>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Industry</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Enquiries</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unique Buyers</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {analytics.geography.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.location || 'N/A'}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.industry || 'N/A'}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.total_enquiries}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.unique_buyers}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {analytics.buyer_insights && analytics.buyer_insights.length > 0 && (
                                <div className="bg-white rounded-xl shadow-lg p-6">
                                    <h3 className="text-lg font-bold text-gray-900 mb-4">Buyer Insights (Views & Clicks)</h3>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Industry</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Views</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Clicks</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unique Visitors</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {analytics.buyer_insights.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.location || 'N/A'}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.industry || 'N/A'}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.total_views}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.total_clicks}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.unique_visitors}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {(!analytics.geography || analytics.geography.length === 0) && (!analytics.buyer_insights || analytics.buyer_insights.length === 0) && (
                                <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                                    <i className="fas fa-map-marker-alt text-4xl text-gray-300 mb-4"></i>
                                    <p className="text-gray-500">No geography data available yet. Data will appear as buyers submit enquiries with location information.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Growth Tab */}
                    {activeTab === 'growth' && analytics.growth && (
                        <div className="bg-white rounded-xl shadow-lg p-6">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Month-on-Month Growth</h3>
                            <p className="text-sm text-gray-600 mb-4">Track whether visibility and engagement are improving over time</p>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Views</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clicks</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Enquiries</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Views Growth</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clicks Growth</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {analytics.growth.map((item, idx) => (
                                            <tr key={idx}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.month}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.views}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.clicks}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.enquiries}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    <span className={item.views_growth >= 0 ? 'text-green-600' : 'text-red-600'}>
                                                        {item.views_growth >= 0 ? '+' : ''}{item.views_growth}%
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    <span className={item.clicks_growth >= 0 ? 'text-green-600' : 'text-red-600'}>
                                                        {item.clicks_growth >= 0 ? '+' : ''}{item.clicks_growth}%
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Enquiries Tab */}
                    {activeTab === 'enquiries' && (
                        <div className="space-y-6">
                            {/* Enquiry Summary */}
                            {analytics.enquiry_summary && (
                                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                                    <div className="bg-white rounded-xl shadow-lg p-4">
                                        <p className="text-sm text-gray-500">Total Enquiries</p>
                                        <p className="text-xl font-bold text-gray-900">{analytics.enquiry_summary.total_enquiries || 0}</p>
                                    </div>
                                    <div className="bg-white rounded-xl shadow-lg p-4">
                                        <p className="text-sm text-gray-500">Unique Buyers</p>
                                        <p className="text-xl font-bold text-gray-900">{analytics.enquiry_summary.unique_buyers || 0}</p>
                                    </div>
                                    <div className="bg-white rounded-xl shadow-lg p-4">
                                        <p className="text-sm text-gray-500">New</p>
                                        <p className="text-xl font-bold text-yellow-600">{analytics.enquiry_summary.new_enquiries || 0}</p>
                                    </div>
                                    <div className="bg-white rounded-xl shadow-lg p-4">
                                        <p className="text-sm text-gray-500">Contacted</p>
                                        <p className="text-xl font-bold text-blue-600">{analytics.enquiry_summary.contacted || 0}</p>
                                    </div>
                                    <div className="bg-white rounded-xl shadow-lg p-4">
                                        <p className="text-sm text-gray-500">Converted</p>
                                        <p className="text-xl font-bold text-green-600">{analytics.enquiry_summary.converted || 0}</p>
                                    </div>
                                </div>
                            )}

                            {/* Enquiries List */}
                            {analytics.enquiries && analytics.enquiries.length > 0 && (
                                <div className="bg-white rounded-xl shadow-lg p-6">
                                    <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Enquiries/RFQs</h3>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Buyer</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Industry</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {analytics.enquiries.map((enquiry, idx) => (
                                                    <tr key={idx}>
                                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{enquiry.product_name}</td>
                                                        <td className="px-6 py-4 text-sm text-gray-900">{enquiry.buyer_name}</td>
                                                        <td className="px-6 py-4 text-sm text-gray-900">{enquiry.buyer_company || 'N/A'}</td>
                                                        <td className="px-6 py-4 text-sm text-gray-900">{enquiry.buyer_location || 'N/A'}</td>
                                                        <td className="px-6 py-4 text-sm text-gray-900">{enquiry.buyer_industry || 'N/A'}</td>
                                                        <td className="px-6 py-4">
                                                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                                                enquiry.status === 'new' ? 'bg-yellow-100 text-yellow-800' :
                                                                enquiry.status === 'contacted' ? 'bg-blue-100 text-blue-800' :
                                                                enquiry.status === 'quoted' ? 'bg-purple-100 text-purple-800' :
                                                                enquiry.status === 'converted' ? 'bg-green-100 text-green-800' :
                                                                'bg-gray-100 text-gray-800'
                                                            }`}>
                                                                {enquiry.status}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-900">
                                                            {new Date(enquiry.created_at).toLocaleDateString()}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {(!analytics.enquiries || analytics.enquiries.length === 0) && (
                                <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                                    <i className="fas fa-envelope text-4xl text-gray-300 mb-4"></i>
                                    <p className="text-gray-500">No enquiries yet. Enquiries will appear here when buyers submit RFQ forms.</p>
                                    <p className="text-sm text-gray-400 mt-2">Note: Enquiry/RFQ forms need to be integrated with the technical team.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Price Requests Tab */}
                    {activeTab === 'price_requests' && (
                        <div className="space-y-6">
                            {/* Price Request Summary */}
                            {analytics.price_request_summary && (
                                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                                    <div className="bg-white rounded-xl shadow-lg p-4">
                                        <p className="text-sm text-gray-500">Total Requests</p>
                                        <p className="text-xl font-bold text-gray-900">{analytics.price_request_summary.total_requests || 0}</p>
                                    </div>
                                    <div className="bg-white rounded-xl shadow-lg p-4">
                                        <p className="text-sm text-gray-500">Unique Buyers</p>
                                        <p className="text-xl font-bold text-gray-900">{analytics.price_request_summary.unique_buyers || 0}</p>
                                    </div>
                                    <div className="bg-white rounded-xl shadow-lg p-4">
                                        <p className="text-sm text-gray-500">Pending</p>
                                        <p className="text-xl font-bold text-yellow-600">{analytics.price_request_summary.pending || 0}</p>
                                    </div>
                                    <div className="bg-white rounded-xl shadow-lg p-4">
                                        <p className="text-sm text-gray-500">Approved</p>
                                        <p className="text-xl font-bold text-green-600">{analytics.price_request_summary.approved || 0}</p>
                                    </div>
                                    <div className="bg-white rounded-xl shadow-lg p-4">
                                        <p className="text-sm text-gray-500">Rejected</p>
                                        <p className="text-xl font-bold text-red-600">{analytics.price_request_summary.rejected || 0}</p>
                                    </div>
                                </div>
                            )}

                            {/* Price Requests by Category */}
                            {analytics.price_requests && analytics.price_requests.length > 0 && (
                                <div className="bg-white rounded-xl shadow-lg p-6">
                                    <h3 className="text-lg font-bold text-gray-900 mb-4">Price Requests by Category</h3>
                                    <p className="text-sm text-gray-600 mb-4">Track which product categories are generating the most price requests</p>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product Type</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Requests</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unique Products</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unique Buyers</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {analytics.price_requests.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 capitalize">{item.category || 'N/A'}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.product_type || 'N/A'}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.total_requests}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.unique_products}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.unique_buyers}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Price Request Growth */}
                            {analytics.price_request_growth && analytics.price_request_growth.length > 0 && (
                                <div className="bg-white rounded-xl shadow-lg p-6">
                                    <h3 className="text-lg font-bold text-gray-900 mb-4">Price Request Growth</h3>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requests</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unique Buyers</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {analytics.price_request_growth.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.month}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.requests}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.unique_buyers}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {(!analytics.price_requests || analytics.price_requests.length === 0) && (
                                <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                                    <i className="fas fa-tag text-4xl text-gray-300 mb-4"></i>
                                    <p className="text-gray-500">No price requests yet. Requests will appear here when buyers click "Request Price" on products.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Basket Quotes Tab */}
                    {activeTab === 'basket_quotes' && (
                        <div className="space-y-6">
                            {/* Basket Quote Summary */}
                            {analytics.basket_quote_summary && (
                                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                                    <div className="bg-white rounded-xl shadow-lg p-4">
                                        <p className="text-sm text-gray-500">Total Quotes</p>
                                        <p className="text-xl font-bold text-gray-900">{analytics.basket_quote_summary.total_quotes || 0}</p>
                                    </div>
                                    <div className="bg-white rounded-xl shadow-lg p-4">
                                        <p className="text-sm text-gray-500">Unique Buyers</p>
                                        <p className="text-xl font-bold text-gray-900">{analytics.basket_quote_summary.unique_buyers || 0}</p>
                                    </div>
                                    <div className="bg-white rounded-xl shadow-lg p-4">
                                        <p className="text-sm text-gray-500">Total Quantity</p>
                                        <p className="text-xl font-bold text-gray-900">{analytics.basket_quote_summary.total_quantity || 0}</p>
                                    </div>
                                    <div className="bg-white rounded-xl shadow-lg p-4">
                                        <p className="text-sm text-gray-500">Pending</p>
                                        <p className="text-xl font-bold text-yellow-600">{analytics.basket_quote_summary.pending || 0}</p>
                                    </div>
                                    <div className="bg-white rounded-xl shadow-lg p-4">
                                        <p className="text-sm text-gray-500">Converted</p>
                                        <p className="text-xl font-bold text-green-600">{analytics.basket_quote_summary.converted || 0}</p>
                                    </div>
                                </div>
                            )}

                            {/* Basket Quotes by Category */}
                            {analytics.basket_quotes && analytics.basket_quotes.length > 0 && (
                                <div className="bg-white rounded-xl shadow-lg p-6">
                                    <h3 className="text-lg font-bold text-gray-900 mb-4">Basket Quotes by Category</h3>
                                    <p className="text-sm text-gray-600 mb-4">Track which product categories are being requested via basket quotes</p>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product Type</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Quotes</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unique Products</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unique Buyers</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Quantity</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {analytics.basket_quotes.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 capitalize">{item.category || 'N/A'}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.product_type || 'N/A'}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.total_quotes}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.unique_products}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.unique_buyers}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.total_quantity}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Basket Quote Growth */}
                            {analytics.basket_quote_growth && analytics.basket_quote_growth.length > 0 && (
                                <div className="bg-white rounded-xl shadow-lg p-6">
                                    <h3 className="text-lg font-bold text-gray-900 mb-4">Basket Quote Growth</h3>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quotes</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unique Buyers</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Quantity</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {analytics.basket_quote_growth.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.month}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.quotes}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.unique_buyers}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.total_quantity}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {(!analytics.basket_quotes || analytics.basket_quotes.length === 0) && (
                                <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                                    <i className="fas fa-shopping-basket text-4xl text-gray-300 mb-4"></i>
                                    <p className="text-gray-500">No basket quotes yet. Quotes will appear here when buyers submit basket quote requests.</p>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </>
    );
}

// Simple line chart component using SVG
function SimpleLineChart({ viewsData, clicksData }) {
    if (!viewsData || viewsData.length === 0) {
        return <div className="flex items-center justify-center h-full text-gray-400">No data available</div>;
    }

    const width = 800;
    const height = 250;
    const padding = { top: 20, right: 30, bottom: 40, left: 50 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Merge data by date
    const dataMap = {};
    viewsData.forEach(d => {
        dataMap[d.date] = { date: d.date, views: parseInt(d.views), clicks: 0 };
    });
    clicksData.forEach(d => {
        if (dataMap[d.date]) {
            dataMap[d.date].clicks = parseInt(d.clicks);
        } else {
            dataMap[d.date] = { date: d.date, views: 0, clicks: parseInt(d.clicks) };
        }
    });

    const data = Object.values(dataMap).sort((a, b) => new Date(a.date) - new Date(b.date));

    if (data.length === 0) {
        return <div className="flex items-center justify-center h-full text-gray-400">No data available</div>;
    }

    const maxViews = Math.max(...data.map(d => d.views), 1);
    const maxClicks = Math.max(...data.map(d => d.clicks), 1);
    const maxValue = Math.max(maxViews, maxClicks);

    const xScale = (i) => padding.left + (i / (data.length - 1 || 1)) * chartWidth;
    const yScale = (v) => padding.top + chartHeight - (v / maxValue) * chartHeight;

    // Generate path
    const viewsPath = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(d.views)}`).join(' ');
    const clicksPath = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(d.clicks)}`).join(' ');

    // X-axis labels
    const xLabels = data.map((d, i) => {
        const date = new Date(d.date);
        return `${date.getMonth() + 1}/${date.getDate()}`;
    });

    return (
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full">
            {/* Grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map(ratio => (
                <line
                    key={ratio}
                    x1={padding.left}
                    y1={padding.top + chartHeight * (1 - ratio)}
                    x2={width - padding.right}
                    y2={padding.top + chartHeight * (1 - ratio)}
                    stroke="#e5e7eb"
                    strokeWidth="1"
                />
            ))}

            {/* Views line */}
            <path d={viewsPath} fill="none" stroke="#3b82f6" strokeWidth="2" />
            {data.map((d, i) => (
                <circle key={`v-${i}`} cx={xScale(i)} cy={yScale(d.views)} r="3" fill="#3b82f6" />
            ))}

            {/* Clicks line */}
            <path d={clicksPath} fill="none" stroke="#10b981" strokeWidth="2" />
            {data.map((d, i) => (
                <circle key={`c-${i}`} cx={xScale(i)} cy={yScale(d.clicks)} r="3" fill="#10b981" />
            ))}

            {/* X-axis labels */}
            {data.map((d, i) => (
                <text
                    key={i}
                    x={xScale(i)}
                    y={height - 10}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#6b7280"
                >
                    {xLabels[i]}
                </text>
            ))}

            {/* Legend */}
            <circle cx={padding.left} cy={10} r="4" fill="#3b82f6" />
            <text x={padding.left + 8} y={14} fontSize="10" fill="#374151">Views</text>
            <circle cx={padding.left + 60} cy={10} r="4" fill="#10b981" />
            <text x={padding.left + 68} y={14} fontSize="10" fill="#374151">Clicks</text>
        </svg>
    );
}

export default KgAnalytics;
